import { useEffect, useState } from 'react';
import { supabase, getCurrentUser } from '../lib/supabase';
import { gradePhenotypes, PhenotypeInput, PhenotypeResult } from '../lib/phenotype';
import { DailyMetrics, PHQ4Entry, WeeklyCheckinEntry } from '../types';
import { generateMockMetrics, KEY_MARKERS } from '../lib/dashboardHelpers';
import { normalizeMarker } from '../lib/labAliases';
import { useDemo } from '../lib/demoContext';
import { getDemoDashboardData } from '../lib/demoData';
import { APPLE_HEALTH_METRIC_NAMES, classifyMetric } from '../lib/metricNames';
import { addToBatch, bestBatchPerDay, computeDelta, latestOrPriorTo, dedupLabs, type BatchMap } from '../lib/dashboardUtils';
import type { DailyHealthSummaryRow, HealthMetricsRow, OuraSleepRow } from '../types/supabase';

export interface AppleExtras {
  restingHR: number | null;
  spo2: number | null;
  distance: number | null;
  weight: number | null;
  weightDelta: number | null;
  activeCalories: number | null;
  daylight: number | null;
  morningLight: { firstTimeStr: string; isMorning: boolean } | null;
  // Body composition
  bmi: number | null;
  bmiDelta: number | null;
  bodyFat: number | null;
  bodyFatDelta: number | null;
  vo2Max: number | null;
  vo2MaxDelta: number | null;
}

/** Per-date maps for Apple Watch metrics — enables period averaging in DashboardAppleWatch */
export interface AppleDailyMaps {
  rhr: Record<string, number>;
  spo2: Record<string, number>;
  distance: Record<string, number>;
  calories: Record<string, number>;
  daylight: Record<string, number>;
  weight: Record<string, number>;
}

export interface HomeLeavingDay {
  date: string;
  leftHome: boolean;
  awayMins: number;
}

export interface DashboardData {
  metrics: DailyMetrics[];
  loading: boolean;
  error: string;
  hasOuraData: boolean;
  appleExtras: AppleExtras;
  appleDailyMaps: AppleDailyMaps;
  keyLabs: { marker: string; value: number; unit: string; ref_low: number | null; ref_high: number | null; panel: string; date: string }[];
  phq4Data: { q1: number; q2: number; q3: number; q4: number } | null;
  phq4Entries: PHQ4Entry[];
  phenoPrimary: { name: string; score: number; color: string } | null;
  phenoPrimaryResult: PhenotypeResult | null;
  phq4ScreeningLevel: 'normal' | 'mild' | 'moderate' | 'severe';
  weeklyCheckins: WeeklyCheckinEntry[];
  homeLeaving: HomeLeavingDay[];
  userAge: number;
  userSex: 'male' | 'female';
  refresh: () => void;
}

export default function useDashboardData(): DashboardData {
  const demo = useDemo();
  const [metrics, setMetrics] = useState<DailyMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasOuraData, setHasOuraData] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [appleExtras, setAppleExtras] = useState<AppleExtras>({
    restingHR: null, spo2: null, distance: null,
    weight: null, weightDelta: null, activeCalories: null, daylight: null, morningLight: null,
    bmi: null, bmiDelta: null, bodyFat: null, bodyFatDelta: null, vo2Max: null, vo2MaxDelta: null,
  });
  const [keyLabs, setKeyLabs] = useState<DashboardData['keyLabs']>([]);
  const [labsLoadedAt, setLabsLoadedAt] = useState(0);
  const [phq4Data, setPhq4Data] = useState<DashboardData['phq4Data']>(null);
  const [phq4Entries, setPhq4Entries] = useState<PHQ4Entry[]>([]);
  const [phenoPrimary, setPhenoPrimary] = useState<DashboardData['phenoPrimary']>(null);
  const [phenoPrimaryResult, setPhenoPrimaryResult] = useState<PhenotypeResult | null>(null);
  const [phq4ScreeningLevel, setPhq4ScreeningLevel] = useState<DashboardData['phq4ScreeningLevel']>('normal');
  const [weeklyCheckins, setWeeklyCheckins] = useState<WeeklyCheckinEntry[]>([]);
  const [homeLeaving, setHomeLeaving] = useState<HomeLeavingDay[]>([]);
  const [userAge, setUserAge] = useState(35);
  const [userSex, setUserSex] = useState<'male' | 'female'>('male');
  const [appleDailyMaps, setAppleDailyMaps] = useState<AppleDailyMaps>({ rhr: {}, spo2: {}, distance: {}, calories: {}, daylight: {}, weight: {} });

  // Re-fetch dashboard data when user returns to the tab after importing labs
  useEffect(() => {
    function handleFocus() {
      try {
        const updatedAt = parseInt(localStorage.getItem('labs_updated_at') || '0', 10);
        if (updatedAt > labsLoadedAt && labsLoadedAt > 0) {
          setRefreshTick(t => t + 1);
        }
      } catch (e) {}
    }
    window.addEventListener('focus', handleFocus);
    // Also check on mount (covers same-tab navigation)
    handleFocus();
    return () => window.removeEventListener('focus', handleFocus);
  }, [labsLoadedAt]);

  // Re-load Oura token whenever auth state changes (handles re-login)
  useEffect(() => {
    if (demo) { setProfileLoaded(true); return; }
    const timeout = setTimeout(() => setProfileLoaded(true), 3000);

    async function loadProfile() {
      try {
        const user = await getCurrentUser();
        if (!user) { setProfileLoaded(true); return; }
        const { data: prof } = await supabase
          .from('profiles')
          .select('oura_token, dob, sex')
          .eq('id', user.id)
          .maybeSingle();
        if (prof?.dob) {
          const born = new Date(prof.dob);
          const age = Math.floor((Date.now() - born.getTime()) / (365.25 * 86400000));
          if (age > 0 && age < 120) setUserAge(age);
        }
        if (prof?.sex === 'female') setUserSex('female');
      } catch (e) { console.error('[Dashboard] profile load error:', e); }
      clearTimeout(timeout);
      setProfileLoaded(true);
    }

    loadProfile();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setProfileLoaded(false);
        loadProfile();
      }
    });

    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  // Main data load
  useEffect(() => {
    if (demo || !profileLoaded) return;
    let cancelled = false;
    const loadTimeout = setTimeout(() => { if (!cancelled) setLoading(false); }, 3000);
    (async () => {
      setLoading(true);
      setError('');
      try {
        const user = await getCurrentUser();
        if (!user) { clearTimeout(loadTimeout); setLoading(false); return; }

        // ── Fetch from daily_health_summary ──
        // Fetch 90 days — enough for 30d view + prior 30d comparison for trend arrows
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 90);
        const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;

        let base: DailyMetrics[] = [];
        // Local flag — avoids stale React state when checking later in the
        // same effect.  The old code used the `hasOuraData` state variable,
        // which was always `false` on first render even after calling
        // setHasOuraData(true), causing Apple Health rows to REPLACE Oura
        // data and wipe out all sleep fields.
        let hasOura = false;

        const { data: summaryRows, error: summaryError } = await supabase
          .from('daily_health_summary')
          .select('date, sleep_score, hrv_rmssd, session_hrv, readiness_score, steps, high_activity_mins, daytime_stress, deep_sleep_mins, rem_sleep_mins, light_sleep_mins, total_sleep_mins, sleep_efficiency, lowest_heart_rate, bedtime_start')
          .eq('user_id', user.id)
          .gte('date', cutoffStr)
          .order('date', { ascending: true });
        if (summaryError) console.error('[Dashboard] daily_health_summary error:', summaryError);

        if (summaryRows && summaryRows.length > 0) {
          hasOura = true;
          setHasOuraData(true);
          base = summaryRows.map((row: Omit<DailyHealthSummaryRow, 'user_id'>): DailyMetrics => ({
            date:             row.date,
            sleep_score:      row.sleep_score,
            hrv:              row.hrv_rmssd ?? row.session_hrv,
            readiness:        row.readiness_score,
            steps:            null,  // Oura undercounts steps — Apple Health is source of truth
            exercise_mins:    row.high_activity_mins != null ? Math.round(row.high_activity_mins) : null,
            stress_score:     row.daytime_stress ?? null,
            deep_sleep_mins:  row.deep_sleep_mins,
            rem_sleep_mins:   row.rem_sleep_mins,
            light_sleep_mins: row.light_sleep_mins,
            total_sleep_mins: row.total_sleep_mins,
            sleep_efficiency: row.sleep_efficiency,
            lowest_hr:        row.lowest_heart_rate,
            bedtime_start:    row.bedtime_start,
          }));
        } else {
          hasOura = false;
          setHasOuraData(false);
          base = generateMockMetrics(30);
        }

        // ── Apple Health ──
        if (user) {
          const { data: appleRows } = await supabase
            .from('health_metrics')
            .select('metric_name, date, value, recorded_at')
            .eq('user_id', user.id)
            .eq('source', 'apple_health')
            .gte('date', cutoffStr)
            .in('metric_name', [...APPLE_HEALTH_METRIC_NAMES])
            .limit(10000);

          if (appleRows && appleRows.length > 0) {
            // ── Dedup helper for metrics with duplicate sync batches ──
            // Multiple syncs create near-duplicate rows with slightly different
            // recorded_at timestamps. Group by (date, MM:SS suffix) to identify
            // sync batches, sum each batch, then take the largest per date.
            const stepsBatch:   BatchMap = {};
            const calBatch:     BatchMap = {};
            const exerciseBatch: BatchMap = {};
            const distBatch:    BatchMap = {};

            const rhrMap:      Record<string, number> = {};
            const hrvMap:      Record<string, number[]> = {};
            const spo2Map:     Record<string, number> = {};
            const weightMap:   Record<string, number> = {};
            const daylightMap: Record<string, number> = {};
            const bmiMap:      Record<string, number> = {};
            const bodyFatMap:  Record<string, number> = {};
            const vo2MaxMap:   Record<string, number> = {};
            const todayDaylightRecs: { date: string; ts: Date }[] = [];

            appleRows.forEach((row: Pick<HealthMetricsRow, 'metric_name' | 'date' | 'value' | 'recorded_at'>) => {
              const category = classifyMetric(row.metric_name);
              const date = row.date;
              const mmss = (row.recorded_at || '').substring(14, 19) || 'xx';
              switch (category) {
                case 'steps':         addToBatch(stepsBatch, date, mmss, row.value); break;
                case 'exercise': {
                  // Native app sends 'Exercise Time' in minutes.
                  // Health Auto Export sends 'apple_exercise_time' in fractional hours.
                  const rawName = (row.metric_name || '').toLowerCase();
                  const isLegacyHours = rawName.includes('apple_exercise_time');
                  const mins = isLegacyHours ? row.value * 60 : row.value;
                  addToBatch(exerciseBatch, date, mmss, mins);
                  break;
                }
                case 'active_energy': addToBatch(calBatch, date, mmss, row.value); break;
                case 'distance':      addToBatch(distBatch, date, mmss, row.value); break;
                case 'resting_hr':    rhrMap[date]      = row.value; break;
                case 'hrv':           if (!hrvMap[date]) hrvMap[date] = []; hrvMap[date].push(row.value); break;
                case 'spo2':          spo2Map[date]     = row.value <= 1 ? row.value * 100 : row.value; break;
                case 'weight':        weightMap[date]   = row.value; break;
                case 'bmi':           bmiMap[date]      = row.value; break;
                case 'body_fat':      bodyFatMap[date]   = row.value; break;
                case 'vo2max':        vo2MaxMap[date]    = row.value; break;
                case 'daylight':
                  daylightMap[date] = (daylightMap[date] || 0) + row.value;
                  if (row.recorded_at) {
                    const ts = new Date(row.recorded_at);
                    if (!isNaN(ts.getTime())) todayDaylightRecs.push({ date, ts });
                  }
                  break;
              }
            });

            // Resolve deduped daily values
            const stepsMap    = bestBatchPerDay(stepsBatch);
            const calMap      = bestBatchPerDay(calBatch);
            const exerciseMap = bestBatchPerDay(exerciseBatch);
            const distMap     = bestBatchPerDay(distBatch);

            const nowForExtras = new Date();
            const todayForExtras = `${nowForExtras.getFullYear()}-${String(nowForExtras.getMonth() + 1).padStart(2, '0')}-${String(nowForExtras.getDate()).padStart(2, '0')}`;
            const fromPrior = (map: Record<string, number>) => latestOrPriorTo(map, todayForExtras);

            const todayDaylightTimes = todayDaylightRecs
              .filter(r => r.date === todayForExtras)
              .map(r => r.ts)
              .sort((a, b) => a.getTime() - b.getTime());
            let morningLight: AppleExtras['morningLight'] = null;
            if (todayDaylightTimes.length > 0) {
              const first = todayDaylightTimes[0];
              const isBackfill = first.getUTCHours() === 12 && first.getUTCMinutes() === 0;
              if (!isBackfill) {
                morningLight = {
                  firstTimeStr: first.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                  isMorning: first.getHours() < 10,
                };
              }
            }

            const weightDates = Object.keys(weightMap).sort();
            let weightDelta: number | null = null;
            if (weightDates.length >= 2) {
              const latestW   = weightMap[weightDates[weightDates.length - 1]];
              const earliestW = weightMap[weightDates[0]];
              weightDelta = Math.round((latestW - earliestW) * 10) / 10;
            }

            // Body composition deltas (same earliest-vs-latest pattern)

            setAppleExtras({
              restingHR:      fromPrior(rhrMap),
              spo2:           fromPrior(spo2Map),
              distance:       fromPrior(distMap),
              weight:         fromPrior(weightMap),
              weightDelta,
              activeCalories: fromPrior(calMap),
              daylight:       fromPrior(daylightMap),
              morningLight,
              bmi:            fromPrior(bmiMap),
              bmiDelta:       computeDelta(bmiMap),
              bodyFat:        fromPrior(bodyFatMap),
              bodyFatDelta:   computeDelta(bodyFatMap),
              vo2Max:         fromPrior(vo2MaxMap),
              vo2MaxDelta:    computeDelta(vo2MaxMap),
            });

            setAppleDailyMaps({
              rhr: { ...rhrMap },
              spo2: { ...spo2Map },
              distance: { ...distMap },
              calories: { ...calMap },
              daylight: { ...daylightMap },
              weight: { ...weightMap },
            });

            // Compute per-day average HRV from Apple Health samples
            const appleHrvAvg: Record<string, number> = {};
            for (const [date, vals] of Object.entries(hrvMap)) {
              appleHrvAvg[date] = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
            }

            // Exclude today's partial data — pillars use prior-day only
            delete stepsMap[todayForExtras];
            delete exerciseMap[todayForExtras];
            delete appleHrvAvg[todayForExtras];

            // If we had no Oura data and only mock metrics, replace with Apple Health dates
            if (!hasOura) {
              const appleDates = new Set([
                ...Object.keys(stepsMap), ...Object.keys(appleHrvAvg),
                ...Object.keys(calMap), ...Object.keys(exerciseMap),
              ]);
              if (appleDates.size > 0) {
                base = Array.from(appleDates).sort().map(date => ({
                  date,
                  sleep_score: null, readiness: null, stress_score: null,
                  deep_sleep_mins: null, rem_sleep_mins: null, light_sleep_mins: null,
                  total_sleep_mins: null, sleep_efficiency: null, lowest_hr: null,
                  bedtime_start: null,
                  hrv: appleHrvAvg[date] ?? null,
                  steps: stepsMap[date] != null ? Math.round(stepsMap[date]) : null,
                  exercise_mins: exerciseMap[date] != null ? Math.round(exerciseMap[date]) : null,
                }));
              }
            } else {
              // Merge Apple Health into Oura base (today already excluded)
              base = base.map(m => ({
                ...m,
                steps:         stepsMap[m.date]    != null ? Math.round(stepsMap[m.date])    : m.steps,
                exercise_mins: exerciseMap[m.date]  != null ? Math.round(exerciseMap[m.date]) : m.exercise_mins,
                hrv:           m.hrv ?? appleHrvAvg[m.date] ?? null,
              }));

              // Add Apple-only dates that Oura doesn't cover
              const ouraDateSet = new Set(base.map(m => m.date));
              Object.keys(stepsMap).forEach(date => {
                if (!ouraDateSet.has(date)) {
                  base.push({
                    date,
                    sleep_score: null, hrv: appleHrvAvg[date] ?? null, readiness: null,
                    stress_score: null, deep_sleep_mins: null,
                    rem_sleep_mins: null, light_sleep_mins: null,
                    total_sleep_mins: null, sleep_efficiency: null, lowest_hr: null,
                    steps: Math.round(stepsMap[date]),
                    exercise_mins: exerciseMap[date] ? Math.round(exerciseMap[date]) : null,
                    bedtime_start: null,
                  });
                }
              });
            }

            base.sort((a, b) => a.date.localeCompare(b.date));
          }
        }

        // ── Sleep data resilience ───────────────────────────────────────────
        // The daily_health_summary view LEFT JOINs oura_sleep, so dates that
        // exist in oura_activity/stress but NOT oura_sleep get null sleep
        // columns. If no row in `base` has sleep data, query oura_sleep
        // directly as a failsafe. This covers: view-level join misses, Oura
        // daily_sleep processing delays, Apple Health rows pushing yesterday
        // past the last Oura sleep date.
        if (hasOura) {
          const hasSleep = base.some(m =>
            (m.sleep_score != null && m.sleep_score > 0) ||
            (m.total_sleep_mins != null && m.total_sleep_mins > 0)
          );

          if (!hasSleep) {
            console.warn('[Dashboard] No sleep data in daily_health_summary — querying oura_sleep directly');
            const { data: sleepRows } = await supabase
              .from('oura_sleep')
              .select('date, score, total_sleep_mins, deep_sleep_mins, rem_sleep_mins, light_sleep_mins, efficiency, lowest_heart_rate, bedtime_start, average_hrv')
              .eq('user_id', user!.id)
              .gte('date', cutoffStr)
              .order('date', { ascending: true });

            if (sleepRows && sleepRows.length > 0) {
              console.log(`[Dashboard] oura_sleep fallback: ${sleepRows.length} rows found`);
              type SleepSelect = Omit<OuraSleepRow, 'user_id'>;
              const sleepByDate: Record<string, SleepSelect> = {};
              sleepRows.forEach((r: SleepSelect) => { sleepByDate[r.date] = r; });

              // Merge sleep data into existing base rows
              base = base.map(m => {
                const sl = sleepByDate[m.date];
                if (!sl) return m;
                return {
                  ...m,
                  sleep_score:      sl.score ?? m.sleep_score,
                  total_sleep_mins: sl.total_sleep_mins ?? m.total_sleep_mins,
                  deep_sleep_mins:  sl.deep_sleep_mins  ?? m.deep_sleep_mins,
                  rem_sleep_mins:   sl.rem_sleep_mins   ?? m.rem_sleep_mins,
                  light_sleep_mins: sl.light_sleep_mins ?? m.light_sleep_mins,
                  sleep_efficiency: sl.efficiency        ?? m.sleep_efficiency,
                  lowest_hr:        sl.lowest_heart_rate ?? m.lowest_hr,
                  bedtime_start:    sl.bedtime_start     ?? m.bedtime_start,
                };
              });

              // Add oura_sleep-only dates not already in base
              const baseDateSet = new Set(base.map(m => m.date));
              sleepRows.forEach((sl: SleepSelect) => {
                if (!baseDateSet.has(sl.date)) {
                  base.push({
                    date:             sl.date,
                    sleep_score:      sl.score,
                    hrv:              sl.average_hrv ?? null,
                    readiness:        null,
                    steps:            null,
                    exercise_mins:    null,
                    stress_score:     null,
                    deep_sleep_mins:  sl.deep_sleep_mins,
                    rem_sleep_mins:   sl.rem_sleep_mins,
                    light_sleep_mins: sl.light_sleep_mins,
                    total_sleep_mins: sl.total_sleep_mins,
                    sleep_efficiency: sl.efficiency,
                    lowest_hr:        sl.lowest_heart_rate,
                    bedtime_start:    sl.bedtime_start,
                  });
                }
              });
              base.sort((a, b) => a.date.localeCompare(b.date));
            } else {
              console.warn('[Dashboard] oura_sleep fallback: no rows found either');
            }
          }
        }

        if (cancelled) return;
        setMetrics(base);

        // ── Key labs ──
        // Fetch ALL labs and normalize marker names at read time.
        // This catches labs stored before normalizeMarker was added at ingest,
        // or with name variants not in the alias list at upload time.
        const { data: labRows } = await supabase
          .from('labs')
          .select('marker, value, unit, ref_low, ref_high, panel, date')
          .eq('user_id', user!.id)
          .order('date', { ascending: false })
          .order('created_at', { ascending: false });

        if (labRows && labRows.length > 0) {
          const keySet = new Set(KEY_MARKERS);
          setKeyLabs(dedupLabs(labRows, keySet, normalizeMarker));
          setLabsLoadedAt(Date.now());
        }

        // ── PHQ-4 ──
        const { data: phq4Rows } = await supabase
          .from('phq4')
          .select('*')
          .eq('user_id', user!.id)
          .order('created_at', { ascending: false })
          .limit(8);

        if (phq4Rows && phq4Rows.length > 0) {
          setPhq4Entries(phq4Rows as PHQ4Entry[]);
          const p = phq4Rows[0];
          setPhq4Data(p);
          const phenoInput: PhenotypeInput = { phq4: { q1: p.q1, q2: p.q2, q3: p.q3, q4: p.q4 } };
          const profile = gradePhenotypes(phenoInput);
          setPhq4ScreeningLevel(profile.screeningLevel);
          if (profile.primary) {
            setPhenoPrimary({ name: profile.primary.name, score: profile.primary.score, color: profile.primary.color });
            setPhenoPrimaryResult(profile.primary);
          }
        }

        // ── Weekly check-ins ──
        const { data: checkinRows } = await supabase
          .from('weekly_checkin')
          .select('*')
          .eq('user_id', user!.id)
          .order('created_at', { ascending: false })
          .limit(12);

        if (checkinRows && checkinRows.length > 0) {
          setWeeklyCheckins(checkinRows as WeeklyCheckinEntry[]);
        }

        // ── Home-leaving (geofence) ──
        const { data: hlRows, error: hlError } = await supabase
          .from('health_metrics')
          .select('date, metric_name, value')
          .eq('user_id', user!.id)
          .eq('source', 'native_app')
          .in('metric_name', ['Left Home', 'Time Away From Home'])
          .gte('date', cutoffStr)
          .order('date', { ascending: true });

        if (hlError) console.error('[Dashboard] home-leaving query error:', hlError);
        if (hlRows && hlRows.length > 0) {
          const byDate: Record<string, { leftHome: boolean; awayMins: number }> = {};
          hlRows.forEach((r: Pick<HealthMetricsRow, 'date' | 'metric_name' | 'value'>) => {
            if (!byDate[r.date]) byDate[r.date] = { leftHome: false, awayMins: 0 };
            if (r.metric_name === 'Left Home') byDate[r.date].leftHome = r.value >= 1;
            if (r.metric_name === 'Time Away From Home') byDate[r.date].awayMins = Math.max(byDate[r.date].awayMins, r.value ?? 0);
          });
          setHomeLeaving(
            Object.entries(byDate)
              .map(([date, d]) => ({ date, ...d }))
              .sort((a, b) => a.date.localeCompare(b.date))
          );
        }

      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : String(e);
        console.error('[Dashboard] load error:', message);
        if (!cancelled) {
          setError('Could not load health data. Check your Oura token in Settings.');
          setMetrics(generateMockMetrics(30));
        }
      }
      if (!cancelled) {
        clearTimeout(loadTimeout);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; clearTimeout(loadTimeout); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoaded, refreshTick]);

  if (demo) return getDemoDashboardData(() => {});

  return {
    metrics, loading, error, hasOuraData, appleExtras, appleDailyMaps, keyLabs,
    phq4Data, phq4Entries, phenoPrimary, phenoPrimaryResult,
    phq4ScreeningLevel, weeklyCheckins, homeLeaving, userAge, userSex,
    refresh: () => setRefreshTick(t => t + 1),
  };
}
